import { useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'antd'
import type { PermissionDiff, PermissionDiffItem } from '@/types'

/** 应用身份。安装包权限 token_type = 0 不进这两块 */
const APP_TOKEN = 1
const USER_TOKEN = 2

export interface PermissionIdentityGroups {
  added: PermissionDiffItem[]
  removed: PermissionDiffItem[]
  unchanged: PermissionDiffItem[]
}

function byScope(a: PermissionDiffItem, b: PermissionDiffItem) {
  return a.scope.localeCompare(b.scope)
}

function groupsOf(
  items: PermissionDiffItem[],
  tokenType: number,
): PermissionDiffItem[] {
  return items.filter((item) => item.token_type === tokenType).sort(byScope)
}

/** 按身份拆 diff。token_type 0 和缺身份的条目不进入任何一块 */
export function splitPermissionDiff(diff: PermissionDiff): {
  app: PermissionIdentityGroups
  user: PermissionIdentityGroups
} {
  const take = (tokenType: number): PermissionIdentityGroups => ({
    added: groupsOf(diff.added, tokenType),
    removed: groupsOf(diff.removed, tokenType),
    unchanged: groupsOf(diff.unchanged_items, tokenType),
  })
  return { app: take(APP_TOKEN), user: take(USER_TOKEN) }
}

function hasAny(groups: PermissionIdentityGroups) {
  return (
    groups.added.length > 0 ||
    groups.removed.length > 0 ||
    groups.unchanged.length > 0
  )
}

function isKnownToken(token: number | undefined): boolean {
  return token === 0 || token === 1 || token === 2
}

/** content：有身份权限；hint：没有可展示权限；none：有变更但身份对不上，不能说成没变更 */
export function permissionPanelMode(diff: PermissionDiff): 'content' | 'hint' | 'none' {
  const { app, user } = splitPermissionDiff(diff)
  if (hasAny(app) || hasAny(user)) return 'content'
  const unclassified = [...diff.added, ...diff.removed].some(
    (item) => !isKnownToken(item.token_type),
  )
  return unclassified ? 'none' : 'hint'
}

/** 草稿抽屉里的权限对比。位置不变，内容按身份和新增 / 删除 / 未调整展示 */
export default function PermissionsSection({
  diff,
  onGoManage,
}: {
  diff: PermissionDiff
  onGoManage: () => void
}) {
  const { t } = useTranslation()
  const { app, user } = splitPermissionDiff(diff)

  const mode = permissionPanelMode(diff)
  const showApp = hasAny(app)
  const showUser = hasAny(user)

  if (mode === 'none') return null

  if (mode === 'hint') {
    return (
      <div style={{ fontSize: 14, lineHeight: '22px', color: '#000' }}>
        {t('versionForm.permHint')}{' '}
        <a
          onClick={onGoManage}
          style={{ color: 'var(--color-brand-indigo)', fontWeight: 500 }}
        >
          [{t('versionForm.permManage')}]
        </a>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showApp && (
        <IdentityBlock
          title={t('versionForm.permApp')}
          sameAsPrev={app.added.length === 0 && app.removed.length === 0}
          groups={app}
        />
      )}
      {showApp && showUser && <div style={dashStyle} />}
      {showUser && (
        <IdentityBlock
          title={t('versionForm.permUser')}
          consent={t('versionForm.permUserConsent')}
          sameAsPrev={user.added.length === 0 && user.removed.length === 0}
          groups={user}
        />
      )}
    </div>
  )
}

function IdentityBlock({
  title,
  consent,
  sameAsPrev,
  groups,
}: {
  title: string
  consent?: string
  sameAsPrev: boolean
  groups: PermissionIdentityGroups
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 500, lineHeight: '22px', color: '#000' }}>
        {title}
      </div>
      {sameAsPrev && (
        <div style={{ fontSize: 13, lineHeight: '20px', color: 'rgba(0,0,0,0.6)' }}>
          {t('versionForm.permSameAsPrev')}
        </div>
      )}
      {consent && <div style={consentStyle}>{consent}</div>}
      {groups.added.length > 0 && (
        <ChangeGroup
          label={t('versionForm.permAddedGroup', { count: groups.added.length })}
          tone="added"
          items={groups.added}
        />
      )}
      {groups.removed.length > 0 && (
        <ChangeGroup
          label={t('versionForm.permRemovedGroup', { count: groups.removed.length })}
          tone="removed"
          items={groups.removed}
        />
      )}
      {groups.unchanged.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={groupLabelStyle}>
              {t('versionForm.permUnchangedGroup', { count: groups.unchanged.length })}
            </span>
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? t('versionForm.permCollapse') : t('versionForm.permViewAll')}
              onClick={() => setOpen((value) => !value)}
              style={toggleStyle}
            >
              <span style={open ? caretUpStyle : caretDownStyle} />
            </button>
          </div>
          {open && <NameList items={groups.unchanged} />}
        </div>
      )}
    </div>
  )
}

const TAG_TONE: Record<'added' | 'removed', CSSProperties> = {
  added: { background: 'rgba(0, 180, 42, 0.08)', color: '#009726' },
  removed: { background: 'rgba(255, 56, 60, 0.08)', color: '#FF2929' },
}

function ChangeGroup({
  label,
  tone,
  items,
}: {
  label: string
  tone: 'added' | 'removed'
  items: PermissionDiffItem[]
}) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={groupLabelStyle}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span
          style={{
            flexShrink: 0,
            padding: '0 12px',
            borderRadius: 999,
            fontSize: 12,
            lineHeight: '18px',
            ...TAG_TONE[tone],
          }}
        >
          {t(tone === 'added' ? 'versionForm.scopeAdded' : 'versionForm.scopeRemoved')}
        </span>
        <NameList items={items} />
      </div>
    </div>
  )
}

function NameList({ items }: { items: PermissionDiffItem[] }) {
  return (
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontSize: 13,
        lineHeight: '20px',
        color: '#000',
        wordBreak: 'break-word',
      }}
    >
      {items.map((item, index) => {
        const label = item.name?.trim() || item.scope
        return (
          <span key={item.scope}>
            {index > 0 ? ', ' : null}
            <Tooltip title={item.scope}>
              <span>{label}</span>
            </Tooltip>
          </span>
        )
      })}
    </span>
  )
}

const groupLabelStyle: CSSProperties = {
  fontSize: 13,
  lineHeight: '20px',
  color: 'rgba(0,0,0,0.6)',
}

const consentStyle: CSSProperties = {
  padding: 8,
  borderRadius: 8,
  background: 'rgba(99, 102, 241, 0.1)',
  fontSize: 12,
  lineHeight: '18px',
  color: '#000',
}

const dashStyle: CSSProperties = {
  height: 1,
  borderBottom: '1px dashed #DCDCDC',
}

const toggleStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  padding: 0,
  flexShrink: 0,
  borderRadius: 6,
  border: '1px solid #DCDCDC',
  background: 'rgba(255, 255, 255, 0.85)',
  cursor: 'pointer',
}

const caretDownStyle: CSSProperties = {
  width: 0,
  height: 0,
  borderLeft: '4px solid transparent',
  borderRight: '4px solid transparent',
  borderTop: '5px solid #000',
}

const caretUpStyle: CSSProperties = {
  width: 0,
  height: 0,
  borderLeft: '4px solid transparent',
  borderRight: '4px solid transparent',
  borderBottom: '5px solid #000',
}
